import * as React from 'react';
import { useEffect, useMemo, useState, useCallback } from 'react';

/** Material UI Packages. */
import { Dialog, Box, Button, Tooltip, Typography } from '@mui/material';
import { DataGridPremium, GridToolbarContainer, GridActionsCellItem } from '@mui/x-data-grid-premium';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweepOutlined';

/** Redux Packages. */
import { useDispatch } from 'react-redux';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { PaperComponent } from 'shared/ui/DraggableDialog';
import DialogHeader from 'shared/ui/DialogHeader';
import CircularLoading from 'shared/ui/CircularLoading';
import {
    CustomAutocompleteCell_TradePartner,
    CustomAutocompleteCell_CodeName,
    CustomAutocompleteCell_Vessel,
    CustomAutocompleteCell_string,
    CustomSelectCell_string,
    DateTypingEditCell,
} from 'shared/ui/RenderCell';
import { formatDateToDateString, parseDateToLocal } from 'shared/lib/dateFormatter';
import { readSocketData, createBulkSocketData } from 'shared/lib/fetchSocket';
import { incotermList, trucking_type, containerSizeList } from 'shared/config/constantsData';
import { objectValueFormatter, looksLikeJsonObject, createObjectValueParser } from 'shared/lib/datagridHelpers';
import useFormatMismatchWarning from 'shared/hooks/useFormatMismatchWarning';
import { TeamEditCell } from 'shared/ui/TeamCell';
import { normalizeTeamOptions, parseTeamValue } from 'shared/lib/teamOptions';

/** Options for the fixed single-select fields (incoterm/shipment type) — same lists FieldRenderer.js uses. */
const OPTIONS_MAP = { incotermList, trucking_type };

/** Number of blank rows the grid starts with when opened. */
const INITIAL_ROW_COUNT = 8;

/** Maximum shipments per bulk request — must match the server cap (_MAX_BULK_ROWS in
 *  websocket ShipmentApplication) so the user is warned before submitting, not after. */
const MAX_BULK_ROWS = 200;

/** Identify one grid cell for error highlighting. */
const buildCellKey = (rowId, field) => `${rowId}::${field}`;

/**
 * The bulk grid is fully flat: one row = one line from MBL/master fields all the way down
 * to a single container/alias. If an HBL has 2 containers, the master+HBL info is repeated
 * on 2 rows and only the container fields differ; if a shipment has 2 HBLs, the master info
 * is repeated across however many rows those HBLs' rows span. On submit, rows are grouped
 * back up by matching values (opus reference → shipment, HBL/HAWB# → house) into the same
 * nested shape the single dialog sends.
 */
const getMasterFields = (dialogConfig) =>
    (dialogConfig.sections || [])
        .filter((section) => !section.usage || section.usage === 'NewShipmentDialog')
        .flatMap((section) => section.fields || [])
        .filter((field) => field.key && field.type !== 'NewShipmentHBL' && field.type !== 'TruckingNewShipmentAlias');

/**
 * House-level (HBL/HAWB) field config. Trucking has no house level: shipper/consignee already
 * live on the master row, and aliases attach directly to the shipment.
 */
const getHouseConfig = (dialogConfig) => {
    if (dialogConfig.type === 'trucking') return null;
    const hblSection = (dialogConfig.sections || []).find((section) => section.usage === 'NewShipmentCNTR');
    if (!hblSection) return null;
    const numberField = {
        key: dialogConfig.keyName[2],
        label: dialogConfig.type === 'ocean' ? 'HBL' : 'HAWB',
        type: 'InputField',
    };
    return {
        label: dialogConfig.type === 'ocean' ? 'HBL' : 'HAWB',
        houseComponentKey: dialogConfig.keyName[0],
        cntrComponentKey: dialogConfig.keyName[1],
        fields: [numberField, ...(hblSection.sections || [])],
    };
};

/** Container/alias-level field config, matching the columns used in the single dialog's CNTR/Alias grid. */
const getContainerConfig = (dialogConfig) => {
    if (dialogConfig.type === 'air') {
        return {
            label: 'ALIAS',
            topLevelKey: null,
            fields: [
                { key: 'alias', label: 'Alias', type: 'text' },
                { key: 'il_opus_vessel_id', label: 'Flight#', type: 'text' },
                { key: 'quantity', label: 'Volume', type: 'number' },
                { key: 'il_opus_volume_unit_id', label: 'Unit', type: 'unit' },
                { key: 'weight', label: 'Weight', type: 'number' },
            ],
        };
    }
    if (dialogConfig.type === 'trucking') {
        return {
            label: 'ALIAS',
            topLevelKey: `${dialogConfig.tableName}_alias`,
            fields: [
                { key: 'alias', label: 'Alias', type: 'text' },
                { key: 'quantity', label: 'Volume', type: 'number' },
                { key: 'il_opus_volume_unit_id', label: 'Unit', type: 'unit' },
                { key: 'weight', label: 'Weight', type: 'number' },
                { key: 'reference', label: 'Reference', type: 'text' },
            ],
        };
    }
    // ocean
    const isOex = dialogConfig.tableName === 'il_ocean_export';
    return {
        label: 'CNTR',
        topLevelKey: null,
        fields: [
            { key: 'cntr_number', label: 'Container', type: 'text' },
            ...(isOex ? [{ key: 'seal_number', label: 'Seal', type: 'text' }] : []),
            { key: 'volume', label: 'Volume', type: 'select', options: containerSizeList },
        ],
    };
};

const createBlankRow = (allFields) => ({
    id: randomId(),
    ...allFields.reduce((row, field) => {
        const blank = field.type === 'DateField' ? null : '';
        return { ...row, [field.key]: blank };
    }, {}),
});

const createBlankRows = (allFields, count = INITIAL_ROW_COUNT) =>
    Array.from({ length: count }, () => createBlankRow(allFields));

const isBlankValue = (value) => value === '' || value === null || value === undefined;

const isRowEmpty = (row, fields) => fields.every((field) => isBlankValue(row[field.key]));

const normalizeGroupKey = (value) => String(value ?? '').trim().toUpperCase();

/** Date cells: our edit component already hands back a Date (or null to clear) — only pasted
 *  text needs parsing. Blank input clears the cell (no warning); unparsable text is rejected. */
const createDateValueParser = (field, notifyMismatch) => (value) => {
    if (value == null || value instanceof Date) return value;
    const trimmed = String(value).trim();
    if (!trimmed) return null;
    const parsed = parseDateToLocal(value);
    if (!parsed) {
        notifyMismatch(field);
        return undefined;
    }
    return parsed;
};

/** Number cells (quantity/weight): reject non-numeric paste instead of silently storing NaN. */
const createNumberValueParser = (field, notifyMismatch) => (value) => {
    if (value === '' || value == null) return '';
    const num = Number(value);
    if (!Number.isFinite(num)) {
        notifyMismatch(field);
        return undefined;
    }
    return num;
};

/** Fixed single-select cells (incoterm/shipment type/container volume): our select edit
 *  component only ever commits a value already in `options` — reject anything else (paste). */
const createOptionValueParser = (field, options, notifyMismatch) => (value) => {
    if (value === '' || value == null) return '';
    if (!options.includes(value)) {
        notifyMismatch(field);
        return undefined;
    }
    return value;
};

/** Team cells (OS-566): the row holds `{ id, name }` like Client. A paste may carry that object
 *  (copied from another team cell), a team id, a team name, or garbage — resolve the first three
 *  against the team list, reject the rest. */
const createTeamValueParser = (field, teams, notifyMismatch) => (value) => {
    const parsed = parseTeamValue(value, teams);
    if (parsed === undefined) {
        notifyMismatch(field);
        return undefined;
    }
    return parsed;
};

/** Free-text cells (InputField, alias, container/HBL numbers, reference, vessel, unit): reject
 *  a paste that's obviously an object-valued cell's JSON (wrong column), otherwise accept as-is. */
const createTextValueParser = (field, notifyMismatch) => (value) => {
    if (typeof value !== 'string') return value;
    if (looksLikeJsonObject(value)) {
        notifyMismatch(field);
        return undefined;
    }
    return value;
};

/** Build a DataGrid column for one field — covers both dialogConfig field types (InputField,
 *  AutocompleteFieldTradePartner, ...) and container/alias field types (text/number/select/unit). */
const buildFlatColumn = (field, { autocompleteOptions, handleCellSearch, notifyMismatch, teams }) => {
    const common = {
        field: field.key,
        headerName: field.label,
        width: field.width || 120,
        editable: true,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
    };

    switch (field.type) {
        case 'AutocompleteFieldTradePartner':
            return {
                ...common,
                width: 140,
                renderCell: (params) => params.value?.name || '',
                renderEditCell: (params) => (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={autocompleteOptions[field.key]?.options || []}
                        handleInputChange={(event, value) => handleCellSearch(field.key, value, field.dataType)}
                    />
                ),
                valueFormatter: objectValueFormatter,
                valueParser: createObjectValueParser(field, notifyMismatch),
            };
        case 'AutocompleteFieldTeam':
            return {
                ...common,
                width: 110,
                renderCell: (params) => params.value?.name || '',
                renderEditCell: (params) => <TeamEditCell params={params} teams={teams} />,
                valueFormatter: objectValueFormatter,
                valueParser: createTeamValueParser(field, teams, notifyMismatch),
            };
        case 'AutocompleteField_CodeName':
            return {
                ...common,
                width: 150,
                renderCell: (params) =>
                    params.value ? `${params.value.name ?? ''}` : '',
                renderEditCell: (params) => (
                    <CustomAutocompleteCell_CodeName
                        params={params}
                        options={autocompleteOptions[field.key]?.options || []}
                        handleInputChange={(event, value) => handleCellSearch(field.key, value, field.dataType)}
                    />
                ),
                valueFormatter: objectValueFormatter,
                valueParser: createObjectValueParser(field, notifyMismatch),
            };
        case 'AutocompleteField_String':
            return {
                ...common,
                width: 140,
                renderEditCell: (params) => (
                    <CustomAutocompleteCell_Vessel
                        params={params}
                        value={params.value}
                        options={autocompleteOptions[field.key]?.options || []}
                        handleInputChange={(event, value) => handleCellSearch(field.key, value, field.dataType)}
                    />
                ),
                valueParser: createTextValueParser(field, notifyMismatch),
            };
        case 'AutocompleteField_Single':
            return {
                ...common,
                width: 100,
                renderEditCell: (params) => (
                    <CustomSelectCell_string params={params} options={OPTIONS_MAP[field.options] || []} />
                ),
                valueParser: createOptionValueParser(field, OPTIONS_MAP[field.options] || [], notifyMismatch),
            };
        case 'DateField':
            return {
                ...common,
                width: 100,
                renderCell: (params) => {
                    if (params.value == null) return null;
                    const date = parseDateToLocal(params.value);
                    return date ? date.toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' }) : null;
                },
                renderEditCell: (params) => <DateTypingEditCell params={params} />,
                valueFormatter: (value) => formatDateToDateString(value) ?? '',
                valueParser: createDateValueParser(field, notifyMismatch),
            };
        case 'number':
            return { ...common, type: 'number', valueParser: createNumberValueParser(field, notifyMismatch) };
        case 'select':
            return {
                ...common,
                renderEditCell: (params) => (
                    <CustomSelectCell_string params={params} options={field.options || []} />
                ),
                valueParser: createOptionValueParser(field, field.options || [], notifyMismatch),
            };
        case 'unit':
            return {
                ...common,
                renderEditCell: (params) => (
                    <CustomAutocompleteCell_string
                        params={params}
                        options={autocompleteOptions[field.key]?.options || []}
                        value={params.value}
                        handleInputChange={(event, value) => handleCellSearch(field.key, value, 'il_opus_volume_unit')}
                    />
                ),
                valueParser: createTextValueParser(field, notifyMismatch),
            };
        default:
            return { ...common, valueParser: createTextValueParser(field, notifyMismatch) };
    }
};

/** Group rows by a key, preserving first-seen order. */
const groupRowsBy = (rows, keyFn) => {
    const groups = [];
    const indexByKey = new Map();
    rows.forEach((row) => {
        const key = keyFn(row);
        if (!indexByKey.has(key)) {
            indexByKey.set(key, groups.length);
            groups.push({ key, rows: [] });
        }
        groups[indexByKey.get(key)].rows.push(row);
    });
    return groups;
};

/** Merge a field's value across a group of duplicate rows — first non-blank value wins. */
const mergeFields = (groupRows, fields) => {
    const merged = {};
    fields.forEach((field) => {
        const source = groupRows.find((row) => !isBlankValue(row[field.key]));
        if (!source) return;
        const value = source[field.key];
        // ETD/ETA는 달력 날짜 — UTC 변환(toISOString) 없이 입력된 연·월·일 그대로 보낸다.
        merged[field.key] = value instanceof Date ? formatDateToDateString(value) : value;
    });
    return merged;
};

export default function BulkNewShipmentDialog({ open, onClose, dialogConfig, tabValue }) {
    const dispatch = useDispatch();

    const masterFields = useMemo(() => getMasterFields(dialogConfig), [dialogConfig]);
    const houseConfig = useMemo(() => getHouseConfig(dialogConfig), [dialogConfig]);
    const containerConfig = useMemo(() => getContainerConfig(dialogConfig), [dialogConfig]);
    const allFields = useMemo(
        () => [...masterFields, ...(houseConfig?.fields || []), ...containerConfig.fields],
        [masterFields, houseConfig, containerConfig]
    );

    const [rows, setRows] = useState(() => createBlankRows(allFields));
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [errorRowIds, setErrorRowIds] = useState(new Set());
    /** Cells (`rowId::field`) left blank in a required column — outlined red until filled in. */
    const [errorCellKeys, setErrorCellKeys] = useState(new Set());
    const [autocompleteOptions, setAutocompleteOptions] = useState({});
    const [submitting, setSubmitting] = useState(false);

    /** Reset the grid whenever the dialog is (re)opened for a shipment type. Also clears any
     *  stuck `submitting` flag from a previous request whose ack never came back (e.g. the
     *  server was mid-restart), which would otherwise leave the SAVE button disabled forever
     *  since this component stays mounted across dialog open/close. */
    useEffect(() => {
        if (open) {
            setRows(createBlankRows(allFields));
            setRowSelectionModel([]);
            setErrorRowIds(new Set());
            setErrorCellKeys(new Set());
            setAutocompleteOptions({});
            setSubmitting(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dialogConfig]);

    /** Search trade partner/location/vessel/unit candidates for the cell currently being edited. */
    const handleCellSearch = useCallback(
        (key, value, dataType) => {
            readSocketData(dispatch, tabValue, 'cell', dataType, key, value, (received) => {
                setAutocompleteOptions((prev) => ({ ...prev, [key]: { options: received } }));
            });
        },
        [dispatch, tabValue]
    );

    /** Load the default option list as soon as an autocomplete cell enters edit mode, so it
     *  shows candidates immediately (same behavior as the regular shipment form's onFocus fetch)
     *  instead of requiring the user to type first. */
    const handleCellEditStart = useCallback(
        (params) => {
            const field = allFields.find((f) => f.key === params.field);
            if (!field) return;
            if (field.type === 'unit') {
                handleCellSearch(field.key, '', 'il_opus_volume_unit');
            } else if (field.dataType) {
                handleCellSearch(field.key, '', field.dataType);
            }
        },
        [allFields, handleCellSearch]
    );

    /** Warn when a copy/paste lands on a cell whose format doesn't match the source value
     *  (e.g. pasting a Client cell into an ETA cell, or an unparsable date/number). */
    const notifyFormatMismatch = useFormatMismatchWarning();

    /** Team list for the Team column (OS-566). Read once when the dialog opens: the picker needs it,
     *  and a pasted team name or id is resolved to its { id, name } against it. */
    const [teams, setTeams] = useState([]);
    useEffect(() => {
        let active = true;
        readSocketData(dispatch, null, 'row', 'il_team', null, null, (received) => {
            if (active) setTeams(normalizeTeamOptions(received));
        });
        return () => {
            active = false;
        };
    }, [dispatch]);

    /** Confirm a client value is actually within this user's team_assignments by re-running the
     *  same team-filtered search the Client field's own autocomplete uses, and checking the id
     *  is still in the results. Needed because a value pasted from Forwarder/Shipper/Consignee
     *  (unrestricted search) can carry a perfectly valid {id,name} shape while not being a
     *  partner this user is allowed to use as Client. */
    const verifyClientAssignment = useCallback(
        (partnerValue) =>
            new Promise((resolve) => {
                readSocketData(
                    dispatch,
                    tabValue,
                    'cell',
                    'il_trade_partner',
                    'il_trade_partner_id_client',
                    partnerValue?.name ?? '',
                    (received) => resolve(Array.isArray(received) && received.some((p) => p.id === partnerValue.id)),
                    () => resolve(false)
                );
            }),
        [dispatch, tabValue]
    );

    const notifyClientNotAssigned = useCallback(() => {
        dispatch(
            setSnackbar({
                open: true,
                message: 'That trade partner is not assigned to your team, so it cannot be used as Client — paste ignored.',
                severity: 'warning',
            })
        );
    }, [dispatch]);

    const handleAddRow = useCallback(() => {
        setRows((prev) => [...prev, createBlankRow(allFields)]);
    }, [allFields]);

    const handleDeleteRow = useCallback((id) => {
        setRows((prev) => prev.filter((row) => row.id !== id));
        setRowSelectionModel((prev) => prev.filter((rowId) => rowId !== id));
    }, []);

    const handleDeleteSelected = useCallback(() => {
        if (rowSelectionModel.length === 0) return;
        setRows((prev) => prev.filter((row) => !rowSelectionModel.includes(row.id)));
        setRowSelectionModel([]);
    }, [rowSelectionModel]);

    const processRowUpdate = useCallback(
        async (newRow, oldRow) => {
            const clientValue = newRow['il_trade_partner_id_client'];
            const clientChanged =
                clientValue && typeof clientValue === 'object' && clientValue !== oldRow?.['il_trade_partner_id_client'];
            if (clientChanged) {
                const isAssigned = await verifyClientAssignment(clientValue);
                if (!isAssigned) {
                    notifyClientNotAssigned();
                    return oldRow;
                }
            }

            setRows((prev) => prev.map((row) => (row.id === newRow.id ? newRow : row)));
            setErrorRowIds((prev) => {
                if (!prev.has(newRow.id)) return prev;
                const next = new Set(prev);
                next.delete(newRow.id);
                return next;
            });
            /** Drop the red outline from this row's required cells as soon as they hold a value,
             *  so the user gets feedback while typing instead of only on the next submit. */
            setErrorCellKeys((prev) => {
                if (prev.size === 0) return prev;
                const filledKeys = ["il_opus_reference_id", "il_trade_partner_id_client", "il_team_id"]
                    .filter((key) => !isBlankValue(newRow[key]))
                    .map((key) => buildCellKey(newRow.id, key))
                    .filter((cellKey) => prev.has(cellKey));
                if (filledKeys.length === 0) return prev;
                const next = new Set(prev);
                filledKeys.forEach((cellKey) => next.delete(cellKey));
                return next;
            });
            return newRow;
        },
        [verifyClientAssignment, notifyClientNotAssigned]
    );

    /** Confirm before closing if the user has entered anything. Ignored while a bulk request is
     *  in flight so the dialog can't be closed (ESC/backdrop) out from under the pending ack. */
    const handleClose = useCallback(() => {
        if (submitting) return;
        const hasData = rows.some((row) => !isRowEmpty(row, allFields));
        if (hasData && !window.confirm('Unsaved changes will be lost. Do you want to continue?')) return;
        onClose();
    }, [submitting, rows, allFields, onClose]);

    const buildContainerEntry = useCallback(
        (row) => {
            const entry = {};
            containerConfig.fields.forEach((field) => {
                const value = row[field.key];
                if (isBlankValue(value)) return;
                entry[field.key] = value;
            });
            return entry;
        },
        [containerConfig]
    );

    const getFieldLabel = useCallback(
        (key) => masterFields.find((field) => field.key === key)?.label || key,
        [masterFields]
    );

    /** Required-field check, mirroring what the single dialog validates before it emits (Client)
     *  plus what only bulk needs (Opus Ref, its grouping key). Returns the cells to outline and
     *  the message to show, or null when everything required is filled in.
     *
     *  Opus Ref is checked per row and short-circuits: it decides which rows form one shipment,
     *  so with a blank ref the client check below would be grouping unrelated rows together.
     *  Client is checked per shipment group, not per row — `mergeFields` takes the first non-blank
     *  value, so repeating only the container fields on a shipment's 2nd+ row is legitimate input,
     *  and a shipment is missing its client only when no row in the group carries one.
     */
    const findMissingRequired = useCallback(
        (filledRows) => {
            const missingRefRows = filledRows.filter((row) => isBlankValue(row["il_opus_reference_id"]));
            if (missingRefRows.length > 0) {
                return {
                    cells: missingRefRows.map((row) => buildCellKey(row.id, "il_opus_reference_id")),
                    message: `"${getFieldLabel("il_opus_reference_id")}" is required on every row.`,
                };
            }

            const missingClientCells = [];
            groupRowsBy(filledRows, (row) => normalizeGroupKey(row["il_opus_reference_id"])).forEach(
                ({ rows: shipmentRows }) => {
                    // `?.id` (not just non-blank): a pasted {id: null, ...} carries the right shape
                    // but no partner the server can resolve — same check the single dialog makes.
                    if (shipmentRows.some((row) => row['il_trade_partner_id_client']?.id)) return;
                    missingClientCells.push(...shipmentRows.map((row) => buildCellKey(row.id, 'il_trade_partner_id_client')));
                }
            );
            if (missingClientCells.length > 0) {
                return {
                    cells: missingClientCells,
                    message: `"${getFieldLabel('il_trade_partner_id_client')}" is required for every shipment.`,
                };
            }

            // Team follows the same per-shipment rule as Client (OS-566): the value is an { id, name } object.
            const missingTeamCells = [];
            groupRowsBy(filledRows, (row) => normalizeGroupKey(row["il_opus_reference_id"])).forEach(
                ({ rows: shipmentRows }) => {
                    if (shipmentRows.some((row) => row['il_team_id']?.id)) return;
                    missingTeamCells.push(...shipmentRows.map((row) => buildCellKey(row.id, 'il_team_id')));
                }
            );
            if (missingTeamCells.length > 0) {
                return {
                    cells: missingTeamCells,
                    message: `"${getFieldLabel('il_team_id')}" is required for every shipment.`,
                };
            }

            return null;
        },
        [getFieldLabel]
    );

    /** Detect fields where 2+ rows in the same shipment (or house) group carry different
     *  non-blank values. Duplicate rows are supposed to repeat identical shipment/house info —
     *  differing values usually mean a typo, and silently picking one would hide it. */
    const findConflicts = useCallback(
        (filledRows) => {
            const normalize = (value) => {
                if (value instanceof Date) return formatDateToDateString(value);
                if (value && typeof value === 'object') return JSON.stringify(value);
                return value;
            };

            const conflicts = [];
            const checkGroup = (groupRows, fields, groupLabel) => {
                fields.forEach((field) => {
                    const filled = groupRows.filter((row) => !isBlankValue(row[field.key]));
                    if (filled.length < 2) return;
                    const distinctValues = new Set(filled.map((row) => normalize(row[field.key])));
                    if (distinctValues.size > 1) {
                        conflicts.push({ rowIds: filled.map((row) => row.id), fieldLabel: field.label, groupLabel });
                    }
                });
            };

            const shipmentGroups = groupRowsBy(filledRows, (row) => normalizeGroupKey(row.il_opus_reference_id));
            shipmentGroups.forEach(({ key: shipmentKey, rows: shipmentRows }) => {
                checkGroup(shipmentRows, masterFields, shipmentKey);
                if (houseConfig) {
                    const houseNumberKey = houseConfig.fields[0].key;
                    const houseGroups = groupRowsBy(shipmentRows, (row) => normalizeGroupKey(row[houseNumberKey]));
                    houseGroups.forEach(({ rows: houseRows }) =>
                        checkGroup(houseRows, houseConfig.fields, shipmentKey)
                    );
                }
            });

            return conflicts;
        },
        [masterFields, houseConfig]
    );

    /** Group flat rows back up into the nested shape the single dialog sends: master fields +
     *  house_component: [{...house fields, cntr_component: [...]}, ...], or a flat alias list
     *  for trucking (no house level). Also returns, per shipment group, the flat row ids it came
     *  from — so a server-side row_number error can be mapped back to grid rows to highlight. */
    const buildBulkPayload = useCallback(
        (filledRows) => {
            const shipmentGroups = groupRowsBy(filledRows, (row) => normalizeGroupKey(row.il_opus_reference_id));

            const payloads = shipmentGroups.map(({ rows: shipmentRows }) => {
                const values = mergeFields(shipmentRows, masterFields);
                if (values.il_opus_reference_id) {
                    values.il_opus_reference_id = String(values.il_opus_reference_id).toUpperCase();
                }

                if (houseConfig) {
                    const houseNumberKey = houseConfig.fields[0].key;
                    const houseGroups = groupRowsBy(shipmentRows, (row) => normalizeGroupKey(row[houseNumberKey]));

                    const houseEntries = houseGroups
                        .map(({ rows: houseRows }) => {
                            const houseEntry = mergeFields(houseRows, houseConfig.fields);
                            const containerEntries = houseRows
                                .map(buildContainerEntry)
                                .filter((entry) => Object.keys(entry).length > 0);
                            if (containerEntries.length > 0) houseEntry[houseConfig.cntrComponentKey] = containerEntries;
                            return houseEntry;
                        })
                        .filter((entry) => Object.keys(entry).length > 0);

                    if (houseEntries.length > 0) values[houseConfig.houseComponentKey] = houseEntries;
                } else {
                    const containerEntries = shipmentRows
                        .map(buildContainerEntry)
                        .filter((entry) => Object.keys(entry).length > 0);
                    if (containerConfig.topLevelKey && containerEntries.length > 0) {
                        values[containerConfig.topLevelKey] = containerEntries;
                    }
                }

                return values;
            });

            const rowIdsByGroup = shipmentGroups.map(({ rows: shipmentRows }) => shipmentRows.map((row) => row.id));
            return { payloads, rowIdsByGroup };
        },
        [masterFields, houseConfig, containerConfig, buildContainerEntry]
    );

    const handleSubmit = useCallback(() => {
        const filledRows = rows.filter((row) => !isRowEmpty(row, masterFields));
        if (filledRows.length === 0) {
            dispatch(setSnackbar({ open: true, message: 'Please enter at least one shipment row.', severity: 'warning' }));
            return;
        }

        /** Required fields first — a blank Opus Ref would make the conflict check below group
         *  unrelated rows into one shipment and report bogus value mismatches. */
        const missingRequired = findMissingRequired(filledRows);
        if (missingRequired) {
            setErrorCellKeys(new Set(missingRequired.cells));
            setErrorRowIds(new Set());
            dispatch(setSnackbar({ open: true, message: missingRequired.message, severity: 'warning' }));
            return;
        }
        setErrorCellKeys(new Set());

        const conflicts = findConflicts(filledRows);
        if (conflicts.length > 0) {
            setErrorRowIds(new Set(conflicts.flatMap((c) => c.rowIds)));
            dispatch(
                setSnackbar({
                    open: true,
                    message: conflicts
                        .map((c) => `"${c.groupLabel}": "${c.fieldLabel}" values don't match across its duplicate rows.`)
                        .join(' / '),
                    severity: 'warning',
                })
            );
            return;
        }
        setErrorRowIds(new Set());

        const { payloads, rowIdsByGroup } = buildBulkPayload(filledRows);
        if (payloads.length > MAX_BULK_ROWS) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: `Up to ${MAX_BULK_ROWS} shipments can be registered at once (currently ${payloads.length}). Please split the batch.`,
                    severity: 'warning',
                })
            );
            return;
        }

        setSubmitting(true);
        createBulkSocketData(
            dispatch,
            tabValue,
            'row',
            dialogConfig.tableName,
            dialogConfig.sendReducerNames,
            payloads,
            (response) => {
                setSubmitting(false);
                dispatch(
                    setSnackbar({
                        open: true,
                        message: response.message || `${response.created?.length ?? 0} shipment(s) registered.`,
                        severity: 'success',
                    })
                );
                setRows(createBlankRows(allFields));
                setRowSelectionModel([]);
                setErrorRowIds(new Set());
                setErrorCellKeys(new Set());
                onClose();
            },
            (response) => {
                setSubmitting(false);
                if (response?.row_number) {
                    const failedIds = rowIdsByGroup[response.row_number - 1];
                    if (failedIds) setErrorRowIds(new Set(failedIds));
                }
            }
        );
    }, [
        rows,
        masterFields,
        findMissingRequired,
        findConflicts,
        buildBulkPayload,
        tabValue,
        dialogConfig,
        allFields,
        dispatch,
        onClose,
    ]);

    const columns = useMemo(
        () => [
            ...allFields.map((field) =>
                buildFlatColumn(field, {
                    autocompleteOptions,
                    handleCellSearch,
                    notifyMismatch: notifyFormatMismatch,
                    teams,
                })
            ),
            {
                field: 'actions',
                type: 'actions',
                headerName: '',
                width: 50,
                cellClassName: 'actions',
                getActions: ({ id }) => [
                    <Tooltip title="Delete row" key="delete">
                        <GridActionsCellItem
                            icon={<DeleteIcon />}
                            label="Delete"
                            onClick={() => handleDeleteRow(id)}
                            color="inherit"
                        />
                    </Tooltip>,
                ],
            },
        ],
        [allFields, autocompleteOptions, handleCellSearch, handleDeleteRow, notifyFormatMismatch, teams]
    );

    const Toolbar = useCallback(
        () => (
            <GridToolbarContainer sx={{ justifyContent: 'space-between', px: 1, mb: 0.5 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button sx={{ color: '#003a52', fontSize: '11px' }} startIcon={<AddIcon />} onClick={handleAddRow}>
                        Add Row
                    </Button>
                    <Button
                        sx={{ color: '#a33', fontSize: '11px' }}
                        startIcon={<DeleteSweepIcon />}
                        onClick={handleDeleteSelected}
                        disabled={rowSelectionModel.length === 0}
                    >
                        Delete Selected
                    </Button>
                </Box>
                <Typography sx={{ fontSize: '11px', color: '#777', pr: 1 }}>
                    One row = one {containerConfig.label.toLowerCase()}. For an HBL/shipment with multiple{' '}
                    {containerConfig.label.toLowerCase()}s, repeat the same {houseConfig ? `${houseConfig.label}/` : ''}
                    shipment info on each row — only the {containerConfig.label.toLowerCase()} fields need to differ.
                </Typography>
            </GridToolbarContainer>
        ),
        [handleAddRow, handleDeleteSelected, rowSelectionModel, houseConfig, containerConfig]
    );

    return (
        <>
            <CircularLoading loading={submitting} />
            <Dialog
                maxWidth="xl"
                fullWidth
                open={open}
                onClose={handleClose}
                PaperProps={{ sx: { borderRadius: '18px' } }}
                PaperComponent={PaperComponent}
            >
                <Box sx={{ height: 650, display: 'flex', flexDirection: 'column' }}>
                    <DialogHeader
                        cursor
                        title={`BULK ${dialogConfig.title}`}
                        onClose={handleClose}
                        onSave={handleSubmit}
                        saveDisabled={submitting}
                    />
                    <Box sx={{ flex: 1, p: 1.5, minHeight: 0, }}>
                        <DataGridPremium
                            rows={rows}
                            columns={columns}
                            rowHeight={33}
                            columnHeaderHeight={33}
                            editMode="cell"
                            checkboxSelection
                            disableRowSelectionOnClick
                            rowSelectionModel={rowSelectionModel}
                            onRowSelectionModelChange={setRowSelectionModel}
                            onCellEditStart={handleCellEditStart}
                            processRowUpdate={processRowUpdate}
                            onProcessRowUpdateError={(err) => console.error(err)}
                            cellSelection
                            slots={{ toolbar: Toolbar }}
                            getRowClassName={(params) => (errorRowIds.has(params.id) ? 'bulk-row-error' : '')}
                            getCellClassName={(params) =>
                                errorCellKeys.has(buildCellKey(params.id, params.field)) ? 'bulk-cell--empty' : ''
                            }
                            sx={{
                                height: '100%',
                                fontSize: 12,
                                borderRadius: '10px',
                                '& .MuiDataGrid-columnHeader': { backgroundColor: '#dfdfdf' },
                                '& .MuiDataGrid-columnHeaderTitleContainer': { justifyContent: 'center', fontWeight: 'bold' },
                                '& .MuiDataGrid-cell': { border: '0.5px solid #dfdfdf', },
                                '& .bulk-row-error': { backgroundColor: '#fdecea' },
                                // 필수값(Opus Ref/Client)이 빈 셀을 빨간 테두리로 드러낸다 —
                                // dutyEntryDetailGridSx의 `duty-detail-cell--empty`와 같은 표시.
                                // 행 단위 충돌 표시(bulk-row-error)보다 뒤에 둬서 셀 배경이 이긴다.
                                '& .bulk-cell--empty': {
                                    outline: '1.5px solid #d32f2f',
                                    outlineOffset: '-1.5px',
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Dialog>
        </>
    );
}
